import React, { useState, useEffect, useMemo } from 'react';
import { toISODate } from '../../utils/date';
import { motion } from 'framer-motion';
import {
  UserIcon,
  CalendarDaysIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  SparklesIcon,
  AcademicCapIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { H2, H3, Body, Button, Badge, Card } from '../ui';
import { Input, Select, TextArea } from '../forms';
import { useChildren, useSuggestObservation } from '../../hooks/api';
import { useLearningOutcomes } from '../../hooks/api/useCurriculum';
import { ObservationCreate, ObservationUpdate, Observation, ObservationMedia, LearningOutcome } from '../../types/api';
import type {
  ObservationSuggestion,
  SuggestedOutcome,
} from '../../services/api/observations';
import EnhancedMediaUpload from './EnhancedMediaUpload';
import SuggestionsPanel from '../ai/SuggestionsPanel';
import LearningOutcomePicker from '../curriculum/LearningOutcomePicker';

interface EnhancedObservationFormProps {
  observation?: Observation;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  defaultChildId?: string;
}

interface FormData {
  child_id: string;
  observation_date: string;
  category: 'general' | 'academic' | 'social' | 'emotional' | 'physical';
  learning_area: string;
  /** UUID of the tagged CAPS LearningOutcome, if any. Only populated when
   * editing an existing observation; the create flow currently has no
   * picker, so it stays empty until the user accepts an AI-suggested
   * outcome. */
  learning_outcome_id?: string;
  narrative: string;
  is_milestone: boolean;
  is_concern: boolean;
  follow_up_required: boolean;
  is_private: boolean;
  tags: string[];
}

const EnhancedObservationForm: React.FC<EnhancedObservationFormProps> = ({
  observation,
  onSubmit,
  onCancel,
  isLoading = false,
  mode,
  defaultChildId,
}) => {
  const { data: childrenData } = useChildren();
  const children = childrenData?.items || [];
  
  const [formData, setFormData] = useState<FormData>({
    child_id: observation?.child_id || defaultChildId || '',
    observation_date: observation?.observation_date
      ? toISODate(new Date(observation.observation_date))
      : toISODate(),
    category: observation?.category || 'general',
    learning_area: observation?.learning_area || '',
    learning_outcome_id: observation?.learning_outcome_id,
    narrative: observation?.narrative || '',
    is_milestone: observation?.is_milestone || false,
    is_concern: observation?.is_concern || false,
    follow_up_required: observation?.follow_up_required || false,
    is_private: observation?.is_private || false,
    tags: observation?.tags || [],
  });
  
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [tagsInput, setTagsInput] = useState<string>(
    observation?.tags?.join(', ') || ''
  );
  const [mediaList, setMediaList] = useState<ObservationMedia[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]); // For create mode
  const [currentStep, setCurrentStep] = useState<'details' | 'media'>('details');

  // ── CAPS outcome picker ──────────────────────────────────────────────
  // We pull the org's outcomes once so we can render the tagged outcome's
  // human-readable description as a chip (the form only stores the UUID).
  // Cached aggressively in the hook so opening/closing the picker doesn't
  // refetch.
  const [outcomePickerOpen, setOutcomePickerOpen] = useState(false);
  const outcomesQuery = useLearningOutcomes();
  const taggedOutcome: LearningOutcome | undefined = useMemo(() => {
    if (!formData.learning_outcome_id) return undefined;
    return (outcomesQuery.data ?? []).find(
      (o) => o.id === formData.learning_outcome_id,
    );
  }, [formData.learning_outcome_id, outcomesQuery.data]);

  // ── AI assist ────────────────────────────────────────────────────────
  // The mutation result is the source of truth for the suggestion list;
  // we only track *dismissed* items locally so the user can prune the panel
  // without re-running the (expensive) Ollama call.
  const suggestObservation = useSuggestObservation();
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(
    new Set(),
  );

  const visibleSuggestions: ObservationSuggestion[] = (
    suggestObservation.data?.suggestions ?? []
  ).filter(s => !dismissedSuggestions.has(`${s.issue}|${s.evidence}`));

  // Outcome suggestions are only useful in discovery mode; once the
  // teacher accepts one, we hide all of them so the panel reflects state.
  const suggestedOutcomes: SuggestedOutcome[] =
    suggestObservation.data?.suggested_outcomes ?? [];

  const handleRequestSuggestions = () => {
    if (!formData.narrative.trim()) return;
    setDismissedSuggestions(new Set());
    suggestObservation.mutate({
      text: formData.narrative,
      // Targeted mode if the form already has an outcome tagged; otherwise
      // discovery mode. The backend handles both shapes.
      learning_outcome_id: formData.learning_outcome_id,
    });
  };

  const handleAcceptSuggestion = (suggestion: ObservationSuggestion) => {
    // Replace the first occurrence of `evidence` with `fix`. If the model
    // quoted text that has since been edited, fall back to appending the fix
    // so the teacher still gets the value.
    setFormData(prev => {
      const idx = prev.narrative.indexOf(suggestion.evidence);
      const next =
        idx === -1
          ? `${prev.narrative.trim()} ${suggestion.fix}`.trim()
          : prev.narrative.slice(0, idx) +
            suggestion.fix +
            prev.narrative.slice(idx + suggestion.evidence.length);
      return { ...prev, narrative: next };
    });
    setDismissedSuggestions(d => {
      const next = new Set(d);
      next.add(`${suggestion.issue}|${suggestion.evidence}`);
      return next;
    });
  };

  const handleDismissSuggestion = (suggestion: ObservationSuggestion) => {
    setDismissedSuggestions(d => {
      const next = new Set(d);
      next.add(`${suggestion.issue}|${suggestion.evidence}`);
      return next;
    });
  };

  const handleAcceptOutcome = (outcome: SuggestedOutcome) => {
    setFormData(prev => ({ ...prev, learning_outcome_id: outcome.outcome_id }));
    // Once the user has tagged an outcome, the suggestion list is stale --
    // re-running for targeted-mode feedback would be ideal, but for v1 we
    // just clear the panel to avoid the user re-applying outcome cards.
    suggestObservation.reset();
  };

  // Get selected child details
  const selectedChild = children.find(child => child.id === formData.child_id);

  useEffect(() => {
    if (observation) {
      setFormData({
        child_id: observation.child_id,
        observation_date: toISODate(new Date(observation.observation_date)),
        category: observation.category,
        learning_area: observation.learning_area || '',
        narrative: observation.narrative,
        is_milestone: observation.is_milestone,
        is_concern: observation.is_concern,
        follow_up_required: observation.follow_up_required,
        is_private: observation.is_private,
        tags: observation.tags || [],
      });
      setTagsInput(observation.tags?.join(', ') || '');
      setMediaList(observation.media || []);
    }
  }, [observation]);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.child_id) {
      newErrors.child_id = 'Please select a child';
    }
    
    if (!formData.observation_date) {
      newErrors.observation_date = 'Please select a date';
    }
    
    if (!formData.narrative.trim()) {
      newErrors.narrative = 'Please enter an observation narrative';
    } else if (formData.narrative.trim().length < 10) {
      newErrors.narrative = 'Narrative must be at least 10 characters long';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const tags = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    const submissionData: any = {
      observation_date: formData.observation_date,
      category: formData.category,
      learning_area: formData.learning_area.trim() || undefined,
      narrative: formData.narrative.trim(),
      is_milestone: formData.is_milestone,
      is_concern: formData.is_concern,
      follow_up_required: formData.follow_up_required,
      is_private: formData.is_private,
      tags,
      media: mode === 'create' ? pendingFiles : mediaList,
    };
    
    if (mode === 'create') {
      submissionData.child_id = formData.child_id;
    }
    
    onSubmit(submissionData);
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCheckboxChange = (field: keyof FormData, checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const handleTagsChange = (value: string) => {
    setTagsInput(value);
    const tags = value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    setFormData(prev => ({ ...prev, tags }));
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'academic': return 'info';
      case 'social': return 'success';
      case 'emotional': return 'warning';
      case 'physical': return 'secondary';
      default: return 'default';
    }
  };

  const categoryOptions = [
    { value: 'general', label: 'General', description: 'General observations and notes' },
    { value: 'academic', label: 'Academic', description: 'Learning and academic progress' },
    { value: 'social', label: 'Social', description: 'Social interactions and skills' },
    { value: 'emotional', label: 'Emotional', description: 'Emotional development and wellbeing' },
    { value: 'physical', label: 'Physical', description: 'Physical development and motor skills' },
  ];

  const steps = [
    { id: 'details', label: 'Observation Details', icon: UserIcon },
    { id: 'media', label: 'Media & Attachments', icon: CalendarDaysIcon },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <H2>{mode === 'create' ? 'Create New Observation' : 'Edit Observation'}</H2>
        <Body className="text-gray-600 dark:text-gray-400 mt-1">
          {mode === 'create'
            ? 'Record a new observation with detailed information and media attachments'
            : 'Update the observation details and media attachments'
          }
        </Body>
      </div>

      {/* Step Navigation */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 overflow-x-auto">
        {steps.map((step, index) => (
          <button
            key={step.id}
            type="button"
            onClick={() => setCurrentStep(step.id as any)}
            className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              currentStep === step.id
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <step.icon className="w-5 h-5" />
            <span className="font-medium text-sm sm:text-base">{step.label}</span>
          </button>
        ))}
      </div>

      {/* Step Content */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {currentStep === 'details' && (
          <div className="space-y-6">
            {/* Child and Date Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  Child <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.child_id}
                  onChange={(e) => handleChange('child_id', e.target.value)}
                  options={[
                    { value: '', label: 'Select a child' },
                    ...children.map(child => ({
                      value: child.id,
                      label: `${child.name} ${child.surname}`,
                    }))
                  ]}
                  error={errors.child_id}
                  disabled={mode === 'edit'}
                />
                {selectedChild && (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Body className="text-sm text-gray-600 dark:text-gray-400">
                      Age: {selectedChild.age} • Gender: {selectedChild.gender === 'M' ? 'Male' : 'Female'}
                    </Body>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                  Observation Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formData.observation_date}
                  onChange={(e) => handleChange('observation_date', e.target.value)}
                  error={errors.observation_date}
                  max={toISODate()}
                />
              </div>
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {categoryOptions.map((option) => (
                  <Card
                    key={option.value}
                    className={`p-4 cursor-pointer transition-all ${
                      formData.category === option.value
                        ? 'ring-2 ring-primary-500 border-primary-200'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => handleChange('category', option.value)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{option.label}</span>
                      <Badge variant={getCategoryColor(option.value) as any} size="sm">
                        {option.label}
                      </Badge>
                    </div>
                    <Body className="text-sm text-gray-600 dark:text-gray-400">
                      {option.description}
                    </Body>
                  </Card>
                ))}
              </div>
            </div>

            {/* Learning Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Learning Area (Optional)
              </label>
              <Input
                value={formData.learning_area}
                onChange={(e) => handleChange('learning_area', e.target.value)}
                placeholder="e.g., Mathematics, Language Arts, Science"
              />
            </div>

            {/* CAPS Outcome — picker for the formal taxonomy. Sits next
                to the free-text Learning Area so teachers can describe
                the topic in their own words AND tag against the
                curriculum tree. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <AcademicCapIcon className="w-4 h-4 inline mr-1" />
                  CAPS Learning Outcome (Optional)
                </label>
                <button
                  type="button"
                  onClick={() => setOutcomePickerOpen(true)}
                  data-testid="open-outcome-picker"
                  className="text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
                >
                  {taggedOutcome ? 'Change' : '+ Tag CAPS outcome'}
                </button>
              </div>

              {taggedOutcome ? (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20"
                  data-testid="tagged-outcome-chip"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">
                        {taggedOutcome.code}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {taggedOutcome.short_description || taggedOutcome.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange('learning_outcome_id', undefined)}
                    data-testid="clear-tagged-outcome"
                    className="p-1 rounded text-gray-500 hover:text-rose-600 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                    title="Remove this outcome tag"
                    aria-label="Remove tagged outcome"
                  >
                    <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              ) : formData.learning_outcome_id && outcomesQuery.isLoading ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Loading tagged outcome…
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Tag this observation against a CAPS outcome to power
                  reports and progress tracking. The AI assist below can
                  also suggest one.
                </p>
              )}
            </div>

            {/* Narrative */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Observation Narrative <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={handleRequestSuggestions}
                  disabled={
                    suggestObservation.isPending ||
                    formData.narrative.trim().length === 0
                  }
                  data-testid="request-suggestions"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Ask the local AI to flag specific issues"
                >
                  <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" />
                  {suggestObservation.isPending ? 'Thinking…' : 'AI suggestions'}
                </button>
              </div>
              <TextArea
                value={formData.narrative}
                onChange={(e) => handleChange('narrative', e.target.value)}
                placeholder="Describe what you observed about the child's behavior, learning, or development..."
                rows={5}
                error={errors.narrative}
                helperText={`${formData.narrative.length}/500 characters`}
                maxLength={500}
              />

              {/* AI suggestions panel — only renders after the user clicks
                  the button. Empty results render an "all good" affirmation. */}
              {(suggestObservation.isPending ||
                suggestObservation.data !== undefined) && (
                <div className="mt-3">
                  <SuggestionsPanel
                    isLoading={suggestObservation.isPending}
                    suggestions={visibleSuggestions}
                    suggestedOutcomes={suggestedOutcomes}
                    onAccept={handleAcceptSuggestion}
                    onDismiss={handleDismissSuggestion}
                    onAcceptOutcome={handleAcceptOutcome}
                  />
                </div>
              )}
              {/* Note: the tagged-outcome confirmation lives up by the
                  picker chip now (see CAPS Learning Outcome section
                  above). No need to repeat it here. */}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <TagIcon className="w-4 h-4 inline mr-1" />
                Tags (Optional)
              </label>
              <Input
                value={tagsInput}
                onChange={(e) => handleTagsChange(e.target.value)}
                placeholder="creativity, teamwork, problem-solving, reading"
              />
              {formData.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" size="sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Observation Flags */}
            <div className="space-y-4">
              <H3 className="text-lg font-medium">Observation Flags</H3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className={`p-4 ${formData.is_milestone ? 'ring-2 ring-green-500 bg-green-50 dark:bg-green-900/20' : ''}`}>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_milestone"
                      checked={formData.is_milestone}
                      onChange={(e) => handleCheckboxChange('is_milestone', e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_milestone" className="ml-3 flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                      <div>
                        <Body className="font-medium">Milestone</Body>
                        <Body className="text-sm text-gray-600 dark:text-gray-400">This represents a significant achievement</Body>
                      </div>
                    </label>
                  </div>
                </Card>

                <Card className={`p-4 ${formData.is_concern ? 'ring-2 ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : ''}`}>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_concern"
                      checked={formData.is_concern}
                      onChange={(e) => handleCheckboxChange('is_concern', e.target.checked)}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_concern" className="ml-3 flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
                      <div>
                        <Body className="font-medium">Concern</Body>
                        <Body className="text-sm text-gray-600 dark:text-gray-400">This observation raises a concern</Body>
                      </div>
                    </label>
                  </div>
                </Card>

                <Card className={`p-4 ${formData.follow_up_required ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="follow_up_required"
                      checked={formData.follow_up_required}
                      onChange={(e) => handleCheckboxChange('follow_up_required', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="follow_up_required" className="ml-3 flex items-center">
                      <ArrowPathIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <div>
                        <Body className="font-medium">Follow-up Required</Body>
                        <Body className="text-sm text-gray-600 dark:text-gray-400">This observation needs follow-up action</Body>
                      </div>
                    </label>
                  </div>
                </Card>

                <Card className={`p-4 ${formData.is_private ? 'ring-2 ring-gray-500 bg-gray-50 dark:bg-gray-700/50' : ''}`}>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_private"
                      checked={formData.is_private}
                      onChange={(e) => handleCheckboxChange('is_private', e.target.checked)}
                      className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_private" className="ml-3 flex items-center">
                      <EyeSlashIcon className="w-5 h-5 text-gray-600 mr-2" />
                      <div>
                        <Body className="font-medium">Private</Body>
                        <Body className="text-sm text-gray-600 dark:text-gray-400">Not visible to guardians</Body>
                      </div>
                    </label>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'media' && (
          <div className="space-y-6">
            <div>
              <H3 className="text-lg font-medium mb-2">Media Attachments</H3>
              <Body className="text-gray-600 dark:text-gray-400 mb-4">
                {mode === 'create'
                  ? 'Prepare media files to be attached once the observation is created'
                  : 'Add photos, videos, or documents to support your observation'
                }
              </Body>
              <EnhancedMediaUpload
                observationId={observation?.id}
                existingMedia={mediaList}
                onMediaUpdate={setMediaList}
                onFilesChange={mode === 'create' ? setPendingFiles : undefined}
                disabled={isLoading}
              />
            </div>
          </div>
        )}
      </motion.div>

      {/* Navigation Buttons */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          {currentStep === 'media' && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCurrentStep('details')}
              disabled={isLoading}
            >
              Back to Details
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {currentStep === 'details' && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCurrentStep('media')}
              disabled={isLoading}
            >
              Next: Media
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {mode === 'create' ? 'Create Observation' : 'Update Observation'}
          </Button>
        </div>
      </div>

      <LearningOutcomePicker
        isOpen={outcomePickerOpen}
        onClose={() => setOutcomePickerOpen(false)}
        onSelect={(outcome) => handleChange('learning_outcome_id', outcome.id)}
        selectedIds={
          formData.learning_outcome_id
            ? new Set([formData.learning_outcome_id])
            : undefined
        }
      />
    </form>
  );
};

export default EnhancedObservationForm;
